import { ApplicationTimeoutError } from "common/errors/ApplicationTimeoutError";
import { SubmittingError } from "common/errors/SubmittingError";
import { generate } from "common/random";
import { CheckoutParams, WidgetSize } from "common/types";
import { updateParamsToCheckoutParams } from "common/utils/parameters";
import { formatParameters, schema } from "common/validation/schema";
import ee from "event-emitter";
import { Emitter } from "event-emitter";
import { CheckoutType, PaymentType } from "univapay-node";

import {
    IFRAME_NAME,
    IFRAME_NAME_CONTAINER,
    IFRAME_ZINDEX,
    LOAD_TIMEOUT,
    SUBMIT_TIMEOUT,
} from "../../common/constants";
import { ConnectorError } from "../../common/errors/ConnectorError";
import { TimeoutError } from "../../common/errors/TimeoutError";
import {
    BaseMessageType,
    CheckoutUpdateOptions,
    CheckoutUpdateParameters,
    ConnectorMessage,
    EnhancedMessageEvent,
    Message,
    MessageBeforeClosingResponse,
    MessageExecute,
    MessageExecutePayment,
    MessageHandshake,
    MessageType,
    MessageUpdateParameters,
    ResourceType,
} from "../../common/Messages";
import { timeout } from "../../common/timeout";
import { development } from "../../common/utils/development";
import { JSONParse, JSONStringify } from "../../common/utils/json";
import { toArray } from "../../common/validation/utils";

import { getInputName, renderInput } from "./templates/ButtonTemplate";
import { PARAMETER_KEY_MAPPING, PARAMETER_VALUE_MAPPING, QUERY_PARAMS_TO_REMOVE } from "./constants";
import { BASE_CHECKOUT_URL, FULL_CHECKOUT_URL, FULL_LINK_FORM_URL } from "./utils";

const cleanQueryParams = (query: string | URLSearchParams, keysToRemove: string[] = []): string => {
    const params = new URLSearchParams(query);

    // Remove by key
    for (const key of keysToRemove) {
        params.delete(key);
    }

    // special case based on the value of checkout
    if (params["checkout"] === CheckoutType.TOKEN) {
        params.delete("amount");
        params.delete("currency");
    }

    return params.toString();
};

const containerIframeStyles = {
    background: "rgba(0, 0, 0, 0.5)",
    width: "100vw",
    height: "100vh",
    top: "0",
    left: "0",
    position: "fixed",
    overflowX: "hidden",
    overflowY: "hidden",
    zIndex: IFRAME_ZINDEX,
};

const dialogIframeStyles = {
    background: "transparent",
    border: "0 none transparent",
    bottom: "0",
    display: "none",
    maxHeight: "100%",
    height: "100%",
    left: "0",
    overflowX: "hidden",
    overflowY: "auto",
    position: "fixed",
    right: "0",
    top: "0",
    visibility: "visible",
    maxWidth: "100%",
    width: "100%",
    zIndex: IFRAME_ZINDEX,
};

const inlineIFrameStyles = {
    border: "0 none transparent",
    display: "none",
    width: "100%",
    visibility: "visible",
};

const generateDialogIFrameStyles = (size: WidgetSize) => {
    if (!size) {
        return dialogIframeStyles;
    }

    const width = (() => {
        switch (size) {
            case WidgetSize.LG:
                return "1080px";
            case WidgetSize.MD:
                return "590px";
            case WidgetSize.SM:
                return "380px";
            case WidgetSize.FULL:
            default:
                return "100%";
        }
    })();

    const height = (() => {
        switch (size) {
            case WidgetSize.LG:
                return "900px";
            case WidgetSize.MD:
                return "800px";
            case WidgetSize.SM:
                return "420px";
            case WidgetSize.FULL:
            default:
                return "100%";
        }
    })();

    const pixelWidth = Number(width.replace("px", ""));
    const pixelHeight = Number(width.replace("px", ""));
    const isOverflowing = pixelWidth > window.screen.width || pixelHeight > window.screen.height;

    return {
        ...dialogIframeStyles,
        height: isOverflowing ? "100%" : height,
        width: isOverflowing ? "100%" : width,
        left: size === WidgetSize.FULL || isOverflowing ? 0 : `calc(50% - ${Number(width.replace("px", "")) / 2}px)`,
        top: size === WidgetSize.FULL || isOverflowing ? 0 : `calc(50% - ${Number(height.replace("px", "")) / 2}px)`,
    };
};

export class Connector {
    private static instances = [];

    public emitter: Emitter;
    public iframe: HTMLIFrameElement;
    public iframeContainer: HTMLDivElement;
    public iframeId: string;

    private connectorId: string;
    private remote: Window;
    private connected = false;
    private initCallbacks: ((...params: unknown[]) => void)[] = [];
    private submitCallbacks: ((...params: unknown[]) => void)[] = [];
    private execute: Message<MessageExecutePayment>;

    private currentParams: Partial<CheckoutParams> = {};

    private threeDsModal: HTMLIFrameElement;

    private originalOverflow: string = null;

    static addInstance(connector: Connector) {
        Connector.instances.push(connector);
    }

    static removeClosedInstance() {
        Connector.instances = Connector.instances.filter((instance) => !!instance.remote);
    }

    static getConnectorByIFrame(iframe: HTMLIFrameElement): Connector {
        if (!Connector.instances?.length) {
            throw new Error("No opened connector found.");
        }

        return Connector.instances.length === 1
            ? Connector.instances[0]
            : Connector.instances.find(
                  (instance) => instance.iframe === iframe || (instance.iframeId && instance.iframeId === iframe?.id)
              );
    }

    constructor(params: Partial<CheckoutParams>) {
        this.connectorId = generate();
        this.emitter = ee({});
        this.emitter.on("checkout:execute", this.executeCheckout);
        this.currentParams = params;
        window.addEventListener("message", this.handleMessage);
    }

    private useLinkForm = () => {
        if (this.currentParams?.widgetSize) {
            return (
                FULL_LINK_FORM_URL.startsWith("http://localhost") ||
                FULL_LINK_FORM_URL.startsWith("https://checkout.gyro-n.money")
            );
        }

        return false;
    };

    private constructLinkFormUrl = () => {
        const { paymentMethods, paymentType, paymentTypes, ...rest } = this.currentParams;

        // turn paymentType, paymentTypes and paymentMethods into paymentMethods
        const updatedPaymentMethods = toArray(
            paymentMethods || paymentTypes,
            paymentType ? [paymentType] : undefined
        ) as PaymentType[];

        this.currentParams = { ...rest, paymentMethods: updatedPaymentMethods };

        const search = Object.keys(this.currentParams)
            .reduce((acc, key) => {
                const value = this.currentParams[key];
                const formattedValue = (() => {
                    if (value === undefined || value === null || typeof value === "function") {
                        return undefined;
                    }

                    if (Array.isArray(value)) {
                        return value.filter(Boolean).join(",");
                    }

                    if (typeof value === "object") {
                        const subFields = Object.keys(value)
                            .filter((key) => value[key] !== undefined && value[key] !== null)
                            .reduce((acc, key) => acc.concat(`${key}=${value[key]}`), []);

                        return subFields?.length ? subFields : undefined;
                    }

                    return `${value}`;
                })();

                if (formattedValue !== null && formattedValue !== undefined && formattedValue !== "") {
                    // For query params that have different names in form checkout
                    const mappedKey = PARAMETER_KEY_MAPPING[key] || key;
                    const mappedValue = PARAMETER_VALUE_MAPPING[key]?.(formatParameters) || formattedValue;

                    return acc.concat(`${mappedKey}=${mappedValue}`);
                }

                return acc;
            }, [])
            .join("&");

        return `${FULL_LINK_FORM_URL}?${cleanQueryParams(search, QUERY_PARAMS_TO_REMOVE)}`;
    };

    open = async (message: Message<MessageExecutePayment>, parentNode?: HTMLElement) => {
        const promise = Promise.race([
            new Promise((resolve, reject) => {
                this.initCallbacks = [resolve, reject];
            }),
            timeout(LOAD_TIMEOUT, new ApplicationTimeoutError(LOAD_TIMEOUT), () => {
                this.initCallbacks = [];
            }),
        ]);

        development(() => console.info("1- Opening checkout..."));
        const checkout = this.createIframe(parentNode);

        this.remote = (checkout as HTMLIFrameElement).contentWindow;
        this.execute = message;

        if (this.iframe) {
            this.iframe.style.display = "block";
        }

        await promise;
        this.finishConnect();

        Connector.addInstance(this);

        if (!parentNode) {
            // overflow is hidden for dialog, not for inline style with a parent
            this.originalOverflow = document.body.style.overflow;
            document.body.style.overflow = "hidden";
        }

        return checkout as HTMLIFrameElement;
    };

    async submit() {
        if (this.submitCallbacks.length) {
            console.warn("Form already submitting. Please wait.");
            return Promise.reject(new SubmittingError("FORM_ALREADY_SUBMITTING"));
        }

        this.sendMessage({ type: MessageType.SUBMIT_CARD_DATA, data: { connectorId: this.connectorId } });

        return Promise.race([
            new Promise((resolve, reject) => {
                this.submitCallbacks = [resolve, reject];
            }),
            timeout(SUBMIT_TIMEOUT, new TimeoutError(SUBMIT_TIMEOUT), () => {
                this.submitCallbacks = [];
            }),
        ]);
    }

    async update(overrides: CheckoutUpdateParameters, options?: CheckoutUpdateOptions) {
        if (!overrides) {
            return;
        }

        const newParams = formatParameters({
            ...this.currentParams,
            ...updateParamsToCheckoutParams(overrides),
        } as CheckoutParams);

        try {
            schema.validateSync(newParams);
        } catch (errors) {
            console.error("Invalid override object found when updating the inline form. Skipping.", errors);
            this.emitter.emit("checkout:update-validation-errors", { errors });
            throw errors;
        }

        this.sendMessage({
            type: MessageType.UPDATE_PARAMETERS,
            data: { connectorId: this.connectorId, overrides, options } as MessageUpdateParameters,
        });
        this.currentParams = newParams;
    }

    close(): void {
        this.connected = false;

        // Do not close for inline form
        if (!this.remote) {
            return;
        }

        document.body.style.overflow = this.originalOverflow;
        this.remote.close();
        this.remote = null;

        if (this.iframe) {
            if (this.iframeContainer) {
                window.document.body.removeChild(this.iframeContainer);
            } else {
                window.document.body.removeChild(this.iframe);
            }
        } else {
            Connector.removeClosedInstance();
        }
    }

    private createIframe(parent: HTMLElement): HTMLIFrameElement {
        const isLinkForm = this.useLinkForm();

        const iframe: HTMLIFrameElement = window.document.createElement("iframe");
        iframe.frameBorder = "0";
        iframe.name = IFRAME_NAME;
        iframe.className = IFRAME_NAME;
        iframe.src = isLinkForm ? this.constructLinkFormUrl() : FULL_CHECKOUT_URL;
        iframe.setAttribute("allowtransparency", "true");
        const iframeId = `${IFRAME_NAME}-${generate(8)}`;
        iframe.id = iframeId;
        this.iframeId = iframeId;
        const styles = parent ? inlineIFrameStyles : generateDialogIFrameStyles(this.currentParams?.widgetSize);
        Object.keys(styles).forEach((key: string) => (iframe.style[key] = styles[key]));
        this.iframe = iframe;

        if (isLinkForm && !parent) {
            const iFrameContainer = window.document.createElement("div");
            iFrameContainer.className = IFRAME_NAME_CONTAINER;
            Object.keys(containerIframeStyles).forEach(
                (key: string) => (iFrameContainer.style[key] = containerIframeStyles[key])
            );
            window.document.body.appendChild(iFrameContainer);
            this.iframeContainer = iFrameContainer;
        }

        (parent || this.iframeContainer || window.document.body).appendChild(iframe);

        return iframe;
    }

    private createThreeDsIframe(): HTMLIFrameElement {
        const iFrame = window.document.createElement("iframe");
        iFrame.name = "three-ds-i-frame-client-wrapper";
        iFrame.style.width = "100vw";
        iFrame.style.height = "100vh";
        iFrame.style.top = "0%";
        iFrame.style.left = "0%";
        iFrame.style.position = "fixed";
        iFrame.style.display = "block";
        iFrame.style.background = "rgba(0, 0, 0, 50%)";
        iFrame.style.border = "0";
        iFrame.frameBorder = "0";
        iFrame.style.zIndex = "9999";
        iFrame.setAttribute("allowtransparency", "true");

        iFrame.setAttribute("src", `${new URL(BASE_CHECKOUT_URL).origin}/redirect/index.html?type=three-ds`);
        window.document.body.appendChild(iFrame);

        this.threeDsModal = iFrame;

        return iFrame;
    }

    private finishConnect = (connectorId?: string): void => {
        this.initCallbacks = [];

        if (connectorId === this.connectorId && this.execute) {
            this.sendMessage(this.execute);
        }
    };

    private handleMessage = (e: EnhancedMessageEvent): void => {
        if (!e || (FULL_CHECKOUT_URL.indexOf(e.origin) === -1 && FULL_LINK_FORM_URL.indexOf(e.origin) === -1)) {
            return;
        }

        if (typeof e.data === "object" && e.data.type === "webpackOk") {
            return;
        }

        try {
            const message = JSONParse<ConnectorMessage>(e.data);
            if (!message) {
                return;
            }

            switch (message.type) {
                case MessageType.HANDSHAKE: {
                    const data = message.data;

                    if (!data.paired || (data.paired && this.connectorId === data.connectorId)) {
                        this.handleHandshake(message);
                    }
                    break;
                }

                case MessageType.OPENED:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:opened");
                    }
                    break;

                case MessageType.BEFORE_CLOSING:
                    if (message.data.connectorId === this.connectorId) {
                        // Prevent closing for false only. Other falsy value should close the widget
                        const shouldClose = this.execute.data.params?.beforeClosing?.() !== false;

                        this.sendMessage({
                            type: MessageType.BEFORE_CLOSING_RESPONSE,
                            data: { connectorId: this.connectorId, shouldClose } as MessageBeforeClosingResponse,
                        });
                    }
                    break;

                case MessageType.CLOSED:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:closed", {
                            failedSubscriptionId: message.data.failedSubscriptionId,
                        });
                    }
                    break;

                case MessageType.SUCCESS:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:success", message.data);
                    }
                    break;

                case MessageType.ERROR:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:error", message.data);
                    }
                    break;

                case MessageType.TOKEN_CREATED:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:token-created", message.data);
                    }
                    break;

                case MessageType.CHARGE_CREATED:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:charge-created", message.data);
                    }
                    break;

                case MessageType.SUBSCRIPTION_CREATED:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:subscription-created", message.data);
                    }
                    break;

                case MessageType.THREE_DS_AUTHORIZATION:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:three-ds-authorization", message.data);
                    }
                    break;

                case MessageType.THREE_DS_AUTHORIZATION_SUCCESS:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:three-ds-authorization-success", message.data);

                        if (this.threeDsModal) {
                            this.threeDsModal.remove();
                            this.threeDsModal = null;
                        }
                    }
                    break;

                case MessageType.THREE_DS_AUTHORIZATION_FAILURE:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:three-ds-authorization-failure", message.data);

                        setTimeout(() => {
                            if (this.threeDsModal) {
                                this.threeDsModal.remove();
                                this.threeDsModal = null;
                            }
                        }, 4000); // Give some time for the customer to read: https://github.com/univapaycast/gopay-checkout/issues/1397
                    }
                    break;

                case MessageType.VALIDATION_ERROR:
                    if (message.data.connectorId === this.connectorId) {
                        this.emitter.emit("checkout:validation-error", message.data);
                    }
                    break;

                case MessageType.RESIZE:
                    if (message.data.connectorId === this.connectorId) {
                        this.iframe.style.height = message.data.height;
                        this.iframe.height = message.data.height;
                    }
                    break;

                case MessageType.THREE_DS_AUTHORIZATION_MODAL: {
                    if (message.data.connectorId === this.connectorId) {
                        if (!this.threeDsModal) {
                            this.createThreeDsIframe();
                            window.addEventListener("message", (event) => {
                                if (event.data.type === "THREE_DS_WRAPPER_INITIALIZATION_OPENED") {
                                    this.threeDsModal.contentWindow.postMessage(
                                        { type: "THREE_DS_WRAPPER_INITIALIZATION", ...message.data },
                                        "*"
                                    );
                                } else if (event.data.type === "THREE_DS_MODAL_OPENED") {
                                    this.emitter.emit("checkout:three-ds-authorization-modal-opened", message.data);
                                }
                            });
                        }
                    }
                    break;
                }

                case MessageType.SUBMITTED: {
                    if (message.data.connectorId === this.connectorId) {
                        const [resolve, reject] = this.submitCallbacks;

                        if (message.errors) {
                            reject?.(message.errors);
                        } else {
                            const hiddenTokenInput = renderInput(
                                getInputName(ResourceType.TRANSACTION_TOKEN),
                                message.data.token
                            );
                            this.iframe.parentElement.appendChild(hiddenTokenInput);

                            if (ResourceType.CHARGE in message.data) {
                                const hiddenChargeInput = renderInput(
                                    getInputName(ResourceType.CHARGE),
                                    message.data[ResourceType.CHARGE] as string
                                );
                                this.iframe.parentElement.appendChild(hiddenChargeInput);
                            }

                            if (ResourceType.SUBSCRIPTION in message.data) {
                                const hiddenSubscriptionInput = renderInput(
                                    getInputName(ResourceType.SUBSCRIPTION),
                                    message.data[ResourceType.SUBSCRIPTION] as string
                                );
                                this.iframe.parentElement.appendChild(hiddenSubscriptionInput);
                            }

                            resolve?.(message.data);
                        }

                        this.submitCallbacks = [];
                    }
                    break;
                }

                default:
                    return;
            }
        } catch (e) {
            this.connected = false;
            this.connectorId = null;
            this.remote = null;

            console.error(e);
            throw new ConnectorError();
        }
    };

    private sendMessage(message: Message<BaseMessageType>): void {
        this.remote.postMessage(
            JSONStringify({ ...message, data: { ...message.data, connectorId: this.connectorId } }),
            this.useLinkForm() ? FULL_LINK_FORM_URL : FULL_CHECKOUT_URL
        );
    }

    private executeCheckout = (message: Message<MessageExecute>): void => {
        this.sendMessage(message);
    };

    private handleHandshake(message: Message<MessageHandshake>): void {
        if (this.connected) {
            return;
        }

        if (message.data.paired) {
            development(() => console.info(`5- Paired (id: ${message.data.connectorId})`, message));
            const [resolve] = this.initCallbacks;
            this.connected = true;

            if (typeof resolve === "function") {
                resolve(message.data.connectorId);
            } else {
                this.finishConnect(message.data.connectorId);
            }
        } else if (this.remote) {
            development(() => console.info(`3- Receiving handshake (id: ${message.data.connectorId})`));
            this.sendMessage(message);
        }
    }
}
